import { ConversationParticipantRef, Message } from '../../conversations/types/Conversation';
import { createOptimisticMessage, PENDING_VISITOR_SENDER_ID } from '../../conversations/utils/optimisticMessages';
import { WebWidgetVisitorState } from '../types/webWidget';

type AttachmentKind = 'image' | 'audio' | 'video' | 'document';

const classifyAttachment = (file: File): AttachmentKind => {
  const mimeType = file.type.toLowerCase();
  const extension = file.name.split('.').pop()?.trim().toLowerCase() ?? '';

  if (mimeType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(extension)) {
    return 'image';
  }

  if (mimeType.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(extension)) {
    return 'audio';
  }

  if (mimeType.startsWith('video/') || ['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(extension)) {
    return 'video';
  }

  return 'document';
};

const buildVisitorSender = (
  visitor: WebWidgetVisitorState | null,
  displayName?: string,
): ConversationParticipantRef => ({
  type: 'customer',
  id: visitor?.id ?? PENDING_VISITOR_SENDER_ID,
  name: displayName?.trim() || visitor?.displayName?.trim() || undefined,
});

export const createWebWidgetOptimisticTextMessage = (input: {
  conversationId: string;
  visitor: WebWidgetVisitorState | null;
  content: string;
  displayName?: string;
}): Message => ({
  ...createOptimisticMessage({
    conversationId: input.conversationId,
    sender: buildVisitorSender(input.visitor, input.displayName),
    content: input.content,
  }),
  source: 'webchat',
});

export const createWebWidgetOptimisticAttachmentMessage = (input: {
  conversationId: string;
  visitor: WebWidgetVisitorState | null;
  content: string;
  file: File;
  displayName?: string;
}): Message => {
  const kind = classifyAttachment(input.file);
  const messageType = kind === 'document' ? 'document' : kind;
  const previewUrl = kind === 'document' ? undefined : URL.createObjectURL(input.file);

  return {
    ...createOptimisticMessage({
      conversationId: input.conversationId,
      sender: buildVisitorSender(input.visitor, input.displayName),
      content: input.content,
      messageType,
    }),
    source: 'webchat',
    mediaUrl: previewUrl,
    mimeType: input.file.type || undefined,
    fileName: input.file.name || undefined,
  };
};
